import Link from "next/link";
import { withRouter } from "next/router";
import React from "react";
import styled from "styled-components";
import { NAV } from "./content";
import {
  width,
  height,
  maxWidth,
  space,
  display,
  position,
  zIndex,
  top,
  left,
  right,
  bottom,
  overflow,
  themeGet,
  opacity
} from "styled-system";
import { Box, Flex, Text } from "rebass";
import SVG from "react-inlinesvg";
import { SkipButton } from "../skip-button";
import { useState } from "react";
import Router from "next/router";
import { theme } from "../../styles/theme";
import { DynamicNextLink } from "../dynamic-next-link";
import { Subscribe } from "../../utils/event-system";

const Header = withRouter(({ router }) => {
  const [open, setOpen] = useState(false);
  const onHome = router.pathname === "/";
  const [visible, setVisible] = useState(!onHome);

  Router.events.on("routeChangeComplete", () => setOpen(false));

  Subscribe("intro.complete", () => {
    setVisible(true);
  });

  return (
    <Wrapper
      className={open ? "open" : "closed"}
      opacity={visible ? 1 : [1, 1, 0]}
      as="header"
      px={[3, 3, 3, 4]}
      py={[2, 2, 2, 4]}
      zIndex={4}
    >
      <SkipButton href="#ada-content-begin">Skip To Content</SkipButton>
      <Inner
        maxWidth={3}
        mx="auto"
        alignItems={["center", "center", "center", "flex-start"]}
        justifyContent="space-between"
      >
        <LogoWrap zIndex={2}>
          <Link href="/index" as="/">
            <a>
              <S_SVG
                width={[150, 150, 150, 227]}
                height={[38, 38, 38, 57]}
                src="/static/images/logo.svg"
              />
            </a>
          </Link>
        </LogoWrap>
        <Hamburger
          width={"22px"}
          display={["block", "block", "block", "none"]}
          position="relative"
          height={"18px"}
          zIndex={2}
          aria-label="Toggle menu"
          as="a"
          href="javascript: void(0);"
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
          <span />
        </Hamburger>
        <NavWrap
          px={[3, 3, 3, 0]}
          pb={[3, 3, 3, 0]}
          as="nav"
          top="0"
          left="0"
          right="0"
          bottom="0"
          position={["fixed", "fixed", "fixed", "static"]}
          overflow="auto"
          display={
            open
              ? ["block", "block", "block", "flex"]
              : ["none", "none", "none", "flex"]
          }
        >
          <NavItems />
          <Text
            p="2px"
            textAlign="center"
            dislpay
            ml={[0, 0, 0, 4]}
            mt={[3, 3, 3, 0]}
          >
            <Link href="/search" as="/search">
              <a>
                <S_SVG
                  width={[20, 20, 20, 14]}
                  height={[20, 20, 20, 14]}
                  src="/static/images/icon-search.svg"
                />
              </a>
            </Link>
          </Text>
        </NavWrap>
      </Inner>
    </Wrapper>
  );
});

const NavItems = withRouter(({ router }) => {
  return (
    <NavList
      pt={[110, 138, 138, 0]}
      display={["block", "block", "block", "flex"]}
      as="ul"
    >
      {NAV.map((item, i) => {
        return (
          <NavItem
            as="li"
            key={i}
            mt={i > 0 ? [2, 2, 2, 0] : 0}
            ml={i > 0 ? [0, 0, 0, 4] : 0}
            textAlign={["center", "center", "center", "left"]}
            textStyle={["caps", "caps"]}
            active={router.pathname === item.href}
            fontFamily={["medium"]}
            fontSize={[5, 5, 5, 1]}
          >
            <DynamicNextLink link={item}>{item.text}</DynamicNextLink>
          </NavItem>
        );
      })}
    </NavList>
  );
});

const LogoWrap = styled(Box)`
  ${zIndex}
`;

const NavWrap = styled(Flex)`
  ${display}
  ${position}
  ${top}
  ${left}
  ${right}
  ${bottom}
  ${overflow}
  background-color: ${themeGet("colors.blue.2")};

  @media screen and (min-width: ${theme.breakpoints[2]}) {
    background: none !important;
  }
`;

const NavList = styled(Flex)`
  ${display}
`;

const Hamburger = styled(Box)`
  ${display}
  ${position}
  ${height}
  ${zIndex}

  span {
    width: 100%;
    height: 2px;
    background: white;
    position: absolute;
    left: 0;

    &:nth-child(1) {
      top: 0;

      .open & {
        display: none;
      }
    }

    &:nth-child(2) {
      top: 8px;

      .open & {
        transform: rotate(45deg);
        transform-origin: center;
      }
    }

    &:nth-child(3) {
      display: none;
      top: 8px;

      .open & {
        display: block;
        transform: rotate(-45deg);
        transform-origin: center;
      }
    }

    &:nth-child(4) {
      bottom: 0;

      .open & {
        display: none;
      }
    }
  }
`;

const S_SVG = styled(SVG)`
  ${width}
  ${height}
  ${space}
  display: inline-block;
  transition: opacity 0.3s cubic-bezier(0.465, 0.183, 0.153, 0.946);

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 100%;
    height: 100%;
    fill: white;
  }
`;

const NavItem = styled(Text)`
  cursor: pointer;
  padding: 2px;

  a {
    display: inline-block;
    border-bottom: 1px solid transparent;
    border-bottom-color: ${props => (props.active ? "white" : "transparent")};
    padding-bottom: 3px;
    transition: opacity 0.3s cubic-bezier(0.465, 0.183, 0.153, 0.946);
  }

  & a:hover {
    opacity: 0.7;
  }
`;

const Wrapper = styled(Box)`
  ${maxWidth}
  ${zIndex}
  ${opacity}
  position: relative;
  transition: 1s opacity;
  background: linear-gradient(-90deg, ${themeGet("colors.blue.1")}, #131834);
  border-bottom: 1px solid ${themeGet("colors.yellow")};

  @media screen and (min-width: ${theme.breakpoints[2]}) {
    border-bottom: none;
    background: none;
    position: absolute;
    width: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Inner = styled(Flex)`
  ${maxWidth}
`;

export { Header };
